import React from 'react';
import { useLocation } from 'react-router-dom';
import { DashboardSkeleton } from './DashboardSkeleton';
import { TableSkeleton } from './TableSkeleton';
import { PatientFormSkeleton, AppointmentFormSkeleton, FormSkeleton } from './FormSkeleton';
import { DetailViewSkeleton } from './CardSkeleton';
import { 
  DashboardViewSkeleton,
  PatientViewSkeleton,
  DoctorViewSkeleton,
  AppointmentViewSkeleton,
  BillingViewSkeleton,
  ReportsViewSkeleton,
  SettingsViewSkeleton,
  FormViewSkeleton,
  DetailViewSkeleton as NewDetailViewSkeleton
} from './ViewSpecificSkeletons';
import { LayoutSkeleton } from './SidebarSkeleton';
import {
  DoctorDashboardSkeleton,
  NurseDashboardSkeleton,
  ReceptionistDashboardSkeleton,
  AccountantDashboardSkeleton,
  AdminDashboardSkeleton,
  PatientDashboardSkeleton
} from './RoleBasedSkeletons';
import {
  InventoryDashboardSkeleton,
  InventoryItemsSkeleton,
  PurchaseOrdersSkeleton
} from './InventorySkeleton';

// Function to get role-based dashboard skeleton
const getRoleBasedDashboardSkeleton = (userRole?: string) => {
  switch (userRole?.toLowerCase()) {
    case 'doctor':
      return <DoctorDashboardSkeleton />;
    case 'nurse':
      return <NurseDashboardSkeleton />;
    case 'receptionist':
      return <ReceptionistDashboardSkeleton />;
    case 'accountant':
      return <AccountantDashboardSkeleton />;
    case 'admin':
      return <AdminDashboardSkeleton />;
    case 'patient':
      return <PatientDashboardSkeleton />;
    default:
      return <DashboardViewSkeleton />;
  }
};

// Smart Page Skeleton that adapts based on the current route
export const PageSkeleton: React.FC<{ userRole?: string }> = ({ userRole }) => {
  const location = useLocation();
  const pathname = location.pathname;

  // Dashboard pages - use role-based skeleton if available
  if (pathname === '/dashboard' || pathname === '/') {
    return getRoleBasedDashboardSkeleton(userRole);
  }

  // Patient Management Views
  if (pathname.includes('/patients')) {
    return (
      <LayoutSkeleton>
        <PatientViewSkeleton />
      </LayoutSkeleton>
    );
  }

  // Doctor Management Views
  if (pathname.includes('/doctors')) {
    return (
      <LayoutSkeleton>
        <DoctorViewSkeleton />
      </LayoutSkeleton>
    );
  }

  // Appointment Views
  if (pathname.includes('/appointments')) {
    return (
      <LayoutSkeleton>
        <AppointmentViewSkeleton />
      </LayoutSkeleton>
    );
  }

  // Billing Views
  if (pathname.includes('/billing') || pathname.includes('/claims')) {
    return (
      <LayoutSkeleton>
        <BillingViewSkeleton />
      </LayoutSkeleton>
    );
  }

  // Reports Views
  if (pathname.includes('/reports')) {
    return (
      <LayoutSkeleton>
        <ReportsViewSkeleton />
      </LayoutSkeleton>
    );
  }

  // Settings and Admin Views
  if (pathname.includes('/admin') || pathname.includes('/settings')) {
    return (
      <LayoutSkeleton>
        <SettingsViewSkeleton />
      </LayoutSkeleton>
    );
  }

  // Form pages
  if (pathname.includes('/new') || pathname.includes('/edit')) {
    if (pathname.includes('patient')) {
      return (
        <LayoutSkeleton>
          <PatientFormSkeleton />
        </LayoutSkeleton>
      );
    }
    if (pathname.includes('appointment')) {
      return (
        <LayoutSkeleton>
          <AppointmentFormSkeleton />
        </LayoutSkeleton>
      );
    }
    return (
      <LayoutSkeleton>
        <FormViewSkeleton fields={8} columns={2} />
      </LayoutSkeleton>
    );
  }

  // Detail pages
  if (pathname.includes('/detail') || pathname.match(/\/\d+$/)) {
    return (
      <LayoutSkeleton>
        <NewDetailViewSkeleton />
      </LayoutSkeleton>
    );
  }

  // Staff Management Views
  if (pathname.includes('/staff')) {
    return (
      <LayoutSkeleton>
        <div className="p-6">
          <TableSkeleton 
            rows={10} 
            columns={6} 
            showActions={true}
            showAvatar={true}
          />
        </div>
      </LayoutSkeleton>
    );
  }

  // Inventory Dashboard
  if (pathname === '/inventory' || pathname === '/inventory/') {
    return <InventoryDashboardSkeleton />;
  }

  // Inventory Items Management
  if (pathname.includes('/inventory/items') || pathname.includes('/inventory/stock-update')) {
    return <InventoryItemsSkeleton />;
  }

  // Purchase Orders
  if (pathname.includes('/purchase-orders')) {
    return <PurchaseOrdersSkeleton />;
  }

  // Other Inventory Views
  if (pathname.includes('/inventory')) {
    return (
      <LayoutSkeleton>
        <div className="p-6">
          <TableSkeleton 
            rows={10} 
            columns={6} 
            showActions={true}
            showAvatar={false}
          />
        </div>
      </LayoutSkeleton>
    );
  }

  // Pharmacy Views
  if (pathname.includes('/pharmacy')) {
    return (
      <LayoutSkeleton>
        <div className="p-6">
          <TableSkeleton 
            rows={10} 
            columns={6} 
            showActions={true}
            showAvatar={false}
          />
        </div>
      </LayoutSkeleton>
    );
  }

  // Laboratory Views
  if (pathname.includes('/laboratory') || pathname.includes('/lab')) {
    return (
      <LayoutSkeleton>
        <div className="p-6">
          <TableSkeleton 
            rows={10} 
            columns={6} 
            showActions={true}
            showAvatar={false}
          />
        </div>
      </LayoutSkeleton>
    );
  }

  // Workflow and complex pages
  if (pathname.includes('/workflow')) {
    return (
      <LayoutSkeleton>
        <div className="p-6 space-y-6">
          {/* Workflow header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-48 h-6 bg-gray-300 rounded animate-pulse"></div>
              <div className="flex space-x-2">
                <div className="w-20 h-8 bg-gray-300 rounded animate-pulse"></div>
                <div className="w-24 h-8 bg-gray-300 rounded animate-pulse"></div>
              </div>
            </div>
            <div className="flex space-x-6 border-b border-gray-200 pb-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
              ))}
            </div>
          </div>
          
          {/* Workflow content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <FormViewSkeleton fields={6} columns={2} />
            </div>
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                  <div className="w-32 h-4 bg-gray-300 rounded animate-pulse mb-3"></div>
                  <div className="w-full h-3 bg-gray-300 rounded animate-pulse mb-2"></div>
                  <div className="w-3/4 h-3 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </LayoutSkeleton>
    );
  }

  // Admissions Views
  if (pathname.includes('/admissions')) {
    return (
      <LayoutSkeleton>
        <div className="p-6">
          <TableSkeleton 
            rows={10} 
            columns={6} 
            showActions={true}
            showAvatar={true}
          />
        </div>
      </LayoutSkeleton>
    );
  }

  // Insurance Views
  if (pathname.includes('/insurance')) {
    return (
      <LayoutSkeleton>
        <div className="p-6">
          <TableSkeleton 
            rows={10} 
            columns={6} 
            showActions={true}
            showAvatar={false}
          />
        </div>
      </LayoutSkeleton>
    );
  }

  // Schedule Management
  if (pathname.includes('/schedule')) {
    return (
      <LayoutSkeleton>
        <AppointmentViewSkeleton />
      </LayoutSkeleton>
    );
  }

  // Medical Records
  if (pathname.includes('/medical-records')) {
    return (
      <LayoutSkeleton>
        <NewDetailViewSkeleton />
      </LayoutSkeleton>
    );
  }

  // Profile Views
  if (pathname.includes('/profile')) {
    return (
      <LayoutSkeleton>
        <NewDetailViewSkeleton />
      </LayoutSkeleton>
    );
  }

  // Default fallback - simple content skeleton with layout
  return (
    <LayoutSkeleton>
      <div className="p-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="w-64 h-8 bg-gray-300 rounded animate-pulse mb-4"></div>
          <div className="w-96 h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
          <div className="w-80 h-4 bg-gray-300 rounded animate-pulse"></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="w-12 h-12 bg-gray-300 rounded-full animate-pulse mb-4"></div>
              <div className="w-3/4 h-5 bg-gray-300 rounded animate-pulse mb-2"></div>
              <div className="w-full h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
              <div className="w-2/3 h-4 bg-gray-300 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    </LayoutSkeleton>
  );
};

export default PageSkeleton;